/**
 * Standard Actions configuration for Dawn.
 *
 * Storefront Renderer injects the Shopify Standard Actions bundle
 * (`window.Shopify.actions.{updateCart,openCart,getCart,鈥`). This file
 * overrides the bundle's built-in Dawn refresh path with an explicit,
 * in-theme version so forks that change Dawn's cart contract keep
 * working. Remove this file and the built-in defaults take over.
 *
 *   - openCart   鈥?opens <cart-drawer>; falls back to /cart.
 *   - updateCart 鈥?after the Storefront API mutation, refreshes the
 *     affected cart sections and publishes `cart-update` so Dawn's
 *     pubsub subscribers react.
 *   - other actions (getCart, etc.) keep the default implementation.
 */

// Cart custom elements that advertise sections via getSectionsToRender().
// If Dawn adds a new cart custom element, add its tag here.
const DAWN_CART_TAGS = ['cart-drawer', 'cart-items', 'cart-drawer-items', 'cart-notification'];

// Sections that Dawn's own pubsub subscribers refresh (cart.js's
// CartItems#onCartUpdate fetches and replaces these directly when
// cart-update fires; cart-drawer.js's renderContents handles the
// drawer body). We skip them here to avoid double-rendering.
// Format is '<element-tag>:<getSectionsToRender entry id>'.
// If you change which sections those subscribers refresh, update this set.
const DAWN_PUBSUB_REFRESHED_SECTIONS = new Set([
  'cart-drawer:cart-drawer',
  'cart-drawer-items:CartDrawer',
  'cart-items:main-cart-items',
]);

// Walk every mounted Dawn cart custom element, collect the sections it
// wants rendered, and dedupe. Returns a Map keyed by section id, each
// entry pointing at the DOM mount and the selector used to extract the
// fresh fragment.
function collectCartSections() {
  const sections = new Map();

  for (const el of document.querySelectorAll(DAWN_CART_TAGS.join(','))) {
    let entries;
    try {
      entries = el.getSectionsToRender?.();
    } catch {
      continue;
    }

    const tag = el.tagName.toLowerCase();
    for (const entry of entries ?? []) {
      if (DAWN_PUBSUB_REFRESHED_SECTIONS.has(`${tag}:${entry.id}`)) continue;

      const sectionId = entry.section ?? entry.id;
      if (!sectionId || sections.has(sectionId)) continue;

      // Two patterns coexist in getSectionsToRender():
      //   - cart-items style: entry.section is the parent Liquid section
      //     id, entry.selector is a child node inside it.
      //   - cart-drawer / cart-notification style: entry.id IS the mount;
      //     there is no parent wrapper.
      const root = entry.section ? document.getElementById(entry.id) : document;
      if (!root) continue;

      const mount = entry.selector
        ? (root.querySelector(entry.selector) ?? (entry.section ? root : null))
        : document.getElementById(entry.id);
      if (!mount) continue;

      sections.set(sectionId, {
        mount,
        extractSelector: entry.selector || '.shopify-section',
      });
    }
  }

  return sections;
}

// After a Storefront API mutation, refresh every Dawn cart section
// that isn't already refreshed by Dawn's own pubsub subscribers, then
// publish 'cart-update' so the subscribers run.
//
// We always fetch /cart.js (with sections= when we have any) so that
// `cartData` is defined for subscribers. quick-add-bulk.js reads
// `event.cartData.items` unconditionally 鈥?publishing without cartData
// makes it throw.
async function refreshDawnCartUI() {
  const sections = collectCartSections();
  const sectionsQuery = sections.size
    ? `?sections=${[...sections.keys()].join(',')}`
    : '';
  // `routes` is a Dawn global, but don't assume it's defined.
  const cartUrl = (typeof routes !== 'undefined' && routes?.cart_url) || '/cart';
  const url = `${cartUrl}.js${sectionsQuery}`;
  const cartData = await fetch(url, { headers: { Accept: 'application/json' } })
    .then((r) => (r.ok ? r.json() : null))
    .catch(() => null);

  if (cartData?.sections) {
    for (const [id, { mount, extractSelector }] of sections) {
      const html = cartData.sections[id];
      if (!html) continue;
      const source = new DOMParser()
        .parseFromString(html, 'text/html')
        .querySelector(extractSelector);
      if (source) mount.replaceChildren(...source.childNodes);
    }
  }

  // Hand off to Dawn's existing subscribers. cartData is the full
  // Cart Ajax payload (items, item_count, token, 鈥? plus sections;
  // quick-add-bulk.js and price-per-item.js read it directly.
  publish(PUB_SUB_EVENTS.cartUpdate, {
    source: 'external-refresh',
    cartData: cartData ?? undefined,
  });
}

function initStandardActions() {
  const actions = window.Shopify?.actions;
  if (!actions) return;

  actions.openCart.configure({
    async handler(defaultHandler) {
      const drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.open === 'function') {
        drawer.open();
        return;
      }
      return defaultHandler();
    },
  });

  actions.updateCart.configure({
    // Dawn doesn't currently listen for shopify:cart:* events, but the
    // bundle requires an eventTarget. document is the conventional root.
    eventTarget: () => document,
    async handler(defaultHandler) {
      const result = await defaultHandler();
      try {
        await refreshDawnCartUI();
      } catch (error) {
        console.error('[Dawn] Standard Actions cart refresh failed; reloading.', error);
        window.location.reload();
      }
      return result;
    },
  });
}

// Run immediately if the standard-actions bundle has already attached
// `Shopify.actions`; otherwise wait for DOMContentLoaded, which fires after
// all module scripts have executed regardless of document order.
if (window.Shopify?.actions) {
  initStandardActions();
} else {
  document.addEventListener('DOMContentLoaded', initStandardActions, { once: true });
}

(function () {
  const BAR_ID = 'DiasharkStickyAddToCart';
  const STYLE_ID = 'DiasharkStickyAddToCartStyle';

  function initStickyAddToCart() {
    if (document.getElementById(BAR_ID)) return;
    if (document.querySelector('main[data-template]')?.dataset.template !== 'product') return;

    const mainButton = document.querySelector('[id^="ProductSubmitButton-"]:not([id*="Sticky"])');
    const productForm = mainButton?.closest('form');
    const productInfo = mainButton?.closest('product-info') || document.querySelector('product-info');
    const mainQuantity = productInfo?.querySelector('.quantity__input[name="quantity"]');

    if (!mainButton || !productForm || !productInfo) return;

    injectStickyAddToCartStyles();

    const bar = document.createElement('div');
    bar.id = BAR_ID;
    bar.className = 'diashark-sticky-atc';
    bar.setAttribute('aria-hidden', 'true');
    bar.innerHTML = `
      <div class="diashark-sticky-atc__inner page-width">
        <div class="diashark-sticky-atc__summary">
          <div class="diashark-sticky-atc__copy">
            <p class="diashark-sticky-atc__title"></p>
            <div class="diashark-sticky-atc__price"></div>
          </div>
        </div>
        <div class="diashark-sticky-atc__actions">
          <input class="diashark-sticky-atc__quantity" type="number" min="1" value="1" aria-label="Quantity">
          <button class="button button--primary diashark-sticky-atc__button" type="button">
            <span></span>
          </button>
        </div>
      </div>
    `;

    document.body.appendChild(bar);

    const title = bar.querySelector('.diashark-sticky-atc__title');
    const price = bar.querySelector('.diashark-sticky-atc__price');
    const quantity = bar.querySelector('.diashark-sticky-atc__quantity');
    const button = bar.querySelector('.diashark-sticky-atc__button');
    const buttonText = button.querySelector('span');

    button.addEventListener('click', function () {
      syncStickyQuantityToMain(quantity, mainQuantity);
      mainButton.click();
    });

    quantity.addEventListener('change', function () {
      normalizeStickyQuantity(quantity);
    });

    const syncAll = function () {
      syncStickyContent(productInfo, title, price);
      syncStickyQuantityFromMain(quantity, mainQuantity);
      syncStickyButton(mainButton, button, buttonText);
      updateStickyPosition(bar);
      updateStickyVisibility(bar, mainButton);
    };

    syncAll();

    document.addEventListener('scroll', function () {
      updateStickyPosition(bar);
      updateStickyVisibility(bar, mainButton);
    }, { passive: true });

    window.addEventListener('resize', function () {
      updateStickyPosition(bar);
      updateStickyVisibility(bar, mainButton);
    });

    mainQuantity?.addEventListener('change', syncAll);
    productForm.addEventListener('change', syncAll);

    const observer = new MutationObserver(syncAll);
    observer.observe(productInfo, {
      attributes: true,
      childList: true,
      subtree: true,
    });

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.variantChange, syncAll);
    }
  }

  function injectStickyAddToCartStyles() {
    if (document.getElementById(STYLE_ID)) return;

    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = `
      .diashark-sticky-atc {
        position: fixed;
        top: var(--diashark-sticky-atc-top, var(--header-height, 0px));
        left: 0;
        right: 0;
        z-index: 3;
        background: rgb(var(--color-background));
        color: rgb(var(--color-foreground));
        border-bottom: 0.1rem solid rgba(var(--color-foreground), 0.12);
        box-shadow: 0 0.8rem 2.4rem rgba(var(--color-shadow), 0.12);
        transform: translateY(-100%);
        opacity: 0;
        visibility: hidden;
        transition: transform var(--duration-default) ease, opacity var(--duration-default) ease, visibility var(--duration-default) ease;
      }

      .diashark-sticky-atc.is-active {
        transform: translateY(0);
        opacity: 1;
        visibility: visible;
      }

      .diashark-sticky-atc__inner {
        display: grid;
        grid-template-columns: minmax(0, 1fr) auto;
        align-items: center;
        gap: 1.6rem;
        min-height: 7.2rem;
        padding-top: 1rem;
        padding-bottom: 1rem;
      }

      .diashark-sticky-atc__copy {
        min-width: 0;
      }

      .diashark-sticky-atc__title {
        margin: 0;
        overflow: hidden;
        font-size: 1.4rem;
        font-weight: 700;
        line-height: 1.25;
        letter-spacing: 0;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .diashark-sticky-atc__price {
        margin-top: 0.2rem;
        font-size: 1.3rem;
      }

      .diashark-sticky-atc__price .price {
        font-size: 1.3rem;
      }

      .diashark-sticky-atc__actions {
        display: flex;
        align-items: center;
        gap: 1rem;
      }

      .diashark-sticky-atc__quantity {
        width: 7.2rem;
        min-height: 4.6rem;
        padding: 0 0.8rem;
        color: rgb(var(--color-foreground));
        text-align: center;
        background-color: rgb(var(--color-background));
        border: 0.1rem solid rgba(var(--color-foreground), 0.2);
        border-radius: var(--inputs-radius);
        font: inherit;
      }

      .diashark-sticky-atc__button {
        min-width: 16rem;
        min-height: 4.6rem;
        margin: 0;
        white-space: nowrap;
      }

      @media screen and (max-width: 749px) {
        .diashark-sticky-atc__inner {
          grid-template-columns: minmax(0, 1fr);
          gap: 0.8rem;
          min-height: 0;
        }

        .diashark-sticky-atc__summary {
          display: none;
        }

        .diashark-sticky-atc__actions {
          display: grid;
          grid-template-columns: 7.2rem minmax(0, 1fr);
          width: 100%;
          gap: 0.8rem;
        }

        .diashark-sticky-atc__button {
          width: 100%;
          min-width: 0;
        }
      }
    `;

    document.head.appendChild(style);
  }

  function syncStickyContent(productInfo, title, price) {
    const sourceTitle = productInfo.querySelector('.product__title h1') || productInfo.querySelector('h1');
    const sourcePrice = productInfo.querySelector('[id^="price-"]');

    if (sourceTitle) title.textContent = sourceTitle.textContent.trim();
    if (sourcePrice ) price.innerHTML = sourcePrice.innerHTML;
  }

  function syncStickyQuantityFromMain(stickyQuantity, mainQuantity) {
    if (!mainQuantity) return;

    copyStickyAttribute('min', mainQuantity, stickyQuantity);
    copyStickyAttribute('max', mainQuantity, stickyQuantity);
    copyStickyAttribute('step', mainQuantity, stickyQuantity);
    stickyQuantity.value = mainQuantity.value || stickyQuantity.min || 1;
    normalizeStickyQuantity(stickyQuantity);
  }

  function syncStickyQuantityToMain(stickyQuantity, mainQuantity) {
    if (!mainQuantity) return;

    normalizeStickyQuantity(stickyQuantity);
    mainQuantity.value = stickyQuantity.value;
    mainQuantity.dispatchEvent(new Event('change', { bubbles: true }));
  }

  function syncStickyButton(mainButton, stickyButton, stickyButtonText) {
    stickyButton.toggleAttribute('disabled', mainButton.hasAttribute('disabled'));

    if (mainButton.getAttribute('aria-disabled') === 'true') {
      stickyButton.setAttribute('aria-disabled', 'true');
    } else {
      stickyButton.removeAttribute('aria-disabled');
    }

    const text = mainButton.querySelector('span')?.textContent?.trim() || mainButton.textContent.trim();
    if (text) stickyButtonText.textContent = text;
  }

  function normalizeStickyQuantity(input) {
    const min = Number(input.getAttribute('min')) || 1;
    const max = Number(input.getAttribute('max')) || Infinity;
    const step = Number(input.getAttribute('step')) || 1;
    let value = Number(input.value) || min;

    value = Math.max(min, Math.min(max, value));
    value = min + Math.round((value - min) / step) * step;
    value = Math.max(min, Math.min(max, value));
    input.value = value;
  }

  function copyStickyAttribute(name, source, target) {
    if (source.hasAttribute(name)) {
      target.setAttribute(name, source.getAttribute(name));
    } else {
      target.removeAttribute(name);
    }
  }

  function updateStickyPosition(bar) {
    const header = document.querySelector('.section-header');
    const headerBottom = header ? Math.max(0, header.getBoundingClientRect().bottom) : 0;
    bar.style.setProperty('--diashark-sticky-atc-top', `${Math.round(headerBottom)}px`);
  }

  function updateStickyVisibility(bar, mainButton) {
    const topOffset = Number.parseFloat(bar.style.getPropertyValue('--diashark-sticky-atc-top')) || 0;
    const buttonRect = mainButton.getBoundingClientRect();
    const shouldShow = buttonRect.bottom <= topOffset;

    bar.classList.toggle('is-active', shouldShow);
    bar.setAttribute('aria-hidden', shouldShow ? 'false' : 'true');
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initStickyAddToCart);
  } else {
    initStickyAddToCart();
  }

  document.addEventListener('shopify:section:load', initStickyAddToCart);
})();
